import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Spinner, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Table, UncontrolledAlert} from "reactstrap";
import { useNavigate } from "react-router-dom";
// import { format } from "date-fns";
import "flatpickr/dist/themes/material_green.css"; // Flatpickr styles

const Countries = ({ isHome = false }) => {
  const [modal, setModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [countriesPerPage] = useState(10);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);
  const [userData, setUserData] = useState(null);
  const [newCountry, setNewCountry] = useState({
    name: "",
  });
  
  // const hiddenFields = [
  //   "_id",
  //   "__v",
  //   "createdAt",
  //   "updatedAt",
  // ];

  const navigate = useNavigate();

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      // Reset form when opening
      setNewCountry({
        name: "",      
      });
    }
  };
  
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const token = sessionStorage.getItem("authToken");

  // Auto-dismiss alerts after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        if (error) setError("");
        if (success) setSuccess("");
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Safe sort function to handle undefined values
  const safeSortByName = (data) => {
    if (!Array.isArray(data)) return [];
    
    return [...data].sort((a, b) => {
      // Handle cases where a or b might be undefined
      if (!a || !b) return 0;
      
      // Handle cases where name property might be undefined
      const nameA = a.name || '';
      const nameB = b.name || '';
      
      return nameA.localeCompare(nameB);
    });
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/countries`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error(`Request error ${response.err}`);
        const data = await response.json();
        
        // Use the safe sort function
        const sortedData = safeSortByName(data);
        setCountries(sortedData);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setError("Error fetching countries: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();
  }, [token]);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("authUser"));
    setUserData(user);
  }, []);

  const handleAddToContract = (country) => {
    // Store the selected country in session storage for use in the contract form
    sessionStorage.setItem('selectedCountry', JSON.stringify(country));
    // Navigate to the add contract page
    navigate('/contracts/:type/add');
  };

  const handleAddCountry = async () => {
    try {
      // Validate required fields
      if (!newCountry.name) {
        setError("Country name is required");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/countries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newCountry),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add country");
      }

      const addedCountry = await response.json();
      setSuccess("Country added successfully!");
      
      // Add the new country to the list and sort safely
      setCountries(prev => safeSortByName([...prev, addedCountry]));
      
      toggleModal();
    } catch (error) {
      console.error("Error adding country:", error);
      setError("Error: " + error.message);
    }
  };

  const handleNewCountryChange = (e) => {
    const { name, value } = e.target;
    setNewCountry({ ...newCountry, [name]: value });
  };

  const confirmDelete = (countryId) => {
    setCountryToDelete({ _id: countryId });
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/countries/${countryToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      setSuccess("Country deleted successfully!");
      setCountries((prevCountries) =>
        prevCountries.filter(
          (country) => country._id !== countryToDelete._id
        )
      );
      toggleDeleteModal();
    } catch (error) {
      console.error("Error deleting country:", error);
      setError("Error: " + error.message);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCountries = countries
    .filter((country) =>
      country && country.name ? 
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) : 
      false
    );

  const indexOfLastCountry = currentPage * countriesPerPage;
  const indexOfFirstCountry = indexOfLastCountry - countriesPerPage;
  const currentCountries = filteredCountries.slice(
    indexOfFirstCountry,
    indexOfLastCountry
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      {/* Manage Countries Header */}
      <Card>
        <CardHeader>Manage Countries</CardHeader>
        <CardBody>
          {/* Search Bar */}
          <Row>
            <Col md={6}>
              <Input
                type="text"
                placeholder="Search by country name..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </Col>
            <Col md={4}></Col>
            <Col md={2} className="float-centre">
              {userData && userData.role !== "director" && (
                <Button color="primary" onClick={toggleModal}>
                  Add Country
                </Button>
              )}
            </Col>
          </Row>
          {/* Countries Table */}
          {loading ? (
            <Spinner color="primary" />
          ) : (
            <Table striped>
              <thead>
                <tr>
                  <th>Country Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCountries.length > 0 ? (
                  currentCountries.map((country, index) => (
                    <tr key={country._id || index}>
                      <td>{country.name || 'Unnamed'}</td>
                      <td>
                        {/* <Button
                          color="outline-success"
                          size="sm"
                          onClick={() => handleAddToContract(country)}
                        >
                          Add to Contract
                        </Button>{" "} */}
                        {userData && userData.role === "IT" && (
                          <Button
                            color="outline-danger"
                            size="sm"
                            onClick={() => confirmDelete(country._id)}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center">
                      Sorry! No Results Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {/* Pagination */}
          <Row>
            <Col className="d-flex justify-content-between align-items-center">
              <Button
                color="secondary"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {Array.from(
                {
                  length: Math.ceil(
                    filteredCountries.length / countriesPerPage
                  ),
                },
                (_, i) => (
                  <Button
                    key={i + 1}
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => paginate(i + 1)}
                  >
                    {i + 1}
                  </Button>
                )
              )}
              <Button
                color="secondary"
                onClick={() => paginate(currentPage + 1)}
                disabled={
                  currentPage ===
                  Math.ceil(filteredCountries.length / countriesPerPage)
                }
              >
                Next
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
      {/* Add Country Modal */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add Country</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="name">Country Name *</Label>
              <Input
                type="text"
                name="name"
                id="name"
                value={newCountry.name}
                onChange={handleNewCountryChange}
                placeholder="Enter country name"
                required
              />
            </FormGroup>            
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddCountry}>Save</Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this country?</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            Yes
          </Button>{" "}
          <Button color="secondary" onClick={toggleDeleteModal}>
            No
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Popup Alert Container */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1050,
        width: '350px',
        maxWidth: '90%'
      }}>
        {/* Error Alert Popup */}
        {error && (
          <UncontrolledAlert color="light" role="alert" className="card border p-0 mb-0 shadow-lg">
            <div className="card-header bg-danger-subtle">
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h5 className="font-size-16 text-danger my-1">
                    Error Alert
                  </h5>
                </div>
                <div className="flex-shrink-0">
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            </div>
            <CardBody>
              <div className="text-center">
                <div className="mb-4">
                  <i className="mdi mdi-alert-circle-outline display-4 text-danger"></i>
                </div>
                <h4 className="alert-heading">Error!</h4>
                <p className="mb-0">
                  {error}
                </p>
              </div>
            </CardBody>
          </UncontrolledAlert>
        )}
        
        {/* Success Alert Popup */}
        {success && (
          <UncontrolledAlert color="light" role="alert" className="card border p-0 mb-0 shadow-lg">
            <div className="card-header bg-success-subtle">
              <div className="d-flex">
                <div className="flex-grow-1">
                  <h5 className="font-size-16 text-success my-1">
                    Success Alert
                  </h5>
                </div>
                <div className="flex-shrink-0">
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccess("")}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            </div>
            <CardBody>
              <div className="text-center">
                <div className="mb-4">
                  <i className="mdi mdi-checkbox-marked-circle-outline display-4 text-success"></i>
                </div>
                <h4 className="alert-heading">Success!</h4>
                <p className="mb-0">
                  {success}
                </p>
              </div>
            </CardBody>
          </UncontrolledAlert>
        )}
      </div>
    </div>
  );
};

export default Countries;